import { styled } from "@mui/material/styles";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

// App brand primary — matches the #0c54a0 used across the app (buttons, borders).
const BRAND = "#0c54a0";

const SIZES = {
  small: { pad: "4px 14px", font: 12.5, track: 3 },
  medium: { pad: "6px 20px", font: 13.5, track: 4 },
};

// A compact pill / segmented toggle: a rounded primary-colored track with a
// white "selected" pill. Flat and understated for a professional look.
// `soft` swaps the solid brand track for a light grey one (dark text, white
// selected pill with a hairline shadow) — for forms where the solid bar is too heavy.
const StyledGroup = styled(ToggleButtonGroup, {
  shouldForwardProp: (prop) => prop !== "brandColor" && prop !== "sizeKey" && prop !== "soft",
})(({ brandColor, sizeKey, soft }) => {
  const s = SIZES[sizeKey] || SIZES.small;
  return {
    backgroundColor: soft ? "#E9EEF5" : brandColor,
    borderRadius: 999,
    padding: s.track,
    gap: 2,
    boxShadow: "none",
    "& .MuiToggleButtonGroup-grouped": {
      border: "0 !important",
      borderRadius: "999px !important",
      margin: 0,
      padding: s.pad,
      textTransform: "none",
      fontWeight: 600,
      fontSize: s.font,
      lineHeight: 1.2,
      letterSpacing: 0.1,
      whiteSpace: "nowrap",
      color: soft ? "#475569" : "#ffffff",
      transition: "background-color 0.18s ease, color 0.18s ease",
      "&:hover": {
        backgroundColor: soft ? "rgba(15,23,42,0.05)" : "rgba(255,255,255,0.15)",
      },
      "&.Mui-selected, &.Mui-selected:hover": {
        backgroundColor: "#ffffff",
        color: brandColor,
        ...(soft ? { boxShadow: "0 1px 2px rgba(15,23,42,0.12)" } : {}),
      },
    },
  };
});

// options: [{ value, label }]. Controlled via value/onChange(newValue).
// size: "small" (default) | "medium".
const PillToggle = ({
  value,
  onChange,
  options = [],
  color = BRAND,
  size = "small",
  soft = false,
  sx,
}) => (
  <StyledGroup
    brandColor={color}
    sizeKey={size}
    soft={soft}
    value={value}
    exclusive
    onChange={(e, next) => next !== null && onChange(next)}
    sx={sx}
  >
    {options.map((opt) => (
      <ToggleButton key={opt.value} value={opt.value} disableRipple>
        {opt.label}
      </ToggleButton>
    ))}
  </StyledGroup>
);

export default PillToggle;
